import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import CharacterStack from "../../../components/CharacterStack/CharacterStack";
import { formatRelativeDate } from "../../../util/formatDate";
import FavouriteButton from "./FavouriteButton.jsx";
import {
  LeafIcon,
  LiveIcon,
  PersonalizeIcon,
  ShieldCheckIcon,
} from "./Icons.jsx";
import s from "./ShopHero.module.css";
import { Skeleton } from "../../../themeV2";

const SOCIAL_PLATFORMS = [
  {
    key: "instagram",
    buildUrl: (handle) => `https://instagram.com/${handle}`,
    label: "Instagram",
    svgProps: {
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
    paths: (
      <>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
      </>
    ),
  },
  {
    key: "twitter",
    buildUrl: (handle) => `https://x.com/${handle}`,
    label: "X / Twitter",
    svgProps: { fill: "currentColor" },
    paths: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    ),
  },
  {
    key: "tiktok",
    buildUrl: (handle) => `https://tiktok.com/@${handle}`,
    label: "TikTok",
    svgProps: { fill: "currentColor" },
    paths: (
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1 0-5.78 2.92 2.92 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.57 6.33 6.33 0 0 0 9.37 22a6.33 6.33 0 0 0 6.33-6.33V9.19a8.16 8.16 0 0 0 4.29 1.2V6.69z" />
    ),
  },
  {
    key: "youtube",
    buildUrl: (value) =>
      value.startsWith("http") ? value : `https://youtube.com/@${value}`,
    label: "YouTube",
    svgProps: { fill: "currentColor" },
    paths: (
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    ),
  },
];

const CHARACTERS_LABEL = "Characters played";

export default function ShopHero({
  avatarUrl,
  avatarSquareUrl,
  bannerUrl,
  caption,
  characters = [],
  charityName,
  hasLivestreamProducts,
  hasVideoAvatar,
  influencerId,
  isLoading,
  name,
  nextEventDate,
  productCount,
  socials,
  videoAvatarUrl,
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current) return;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) {
      videoRef.current.pause();
    }
  }, []);

  return (
    <section
      className={`${s.shopHero} ${s.hasBanner}`}
      style={{ backgroundImage: `url(${bannerUrl})` }}
    >
      <div className={s.container}>
        <div className={s.content}>
          {isLoading ? (
            <>
              <Skeleton
                variant="rect"
                tone="onDark"
                style={{
                  width: 220,
                  height: 300,
                  borderRadius: "var(--shape-sm)",
                  flexShrink: 0,
                }}
              />
              <div className={s.details}>
                <Skeleton
                  variant="circle"
                  tone="onDark"
                  style={{ width: 96, height: 96, flexShrink: 0 }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Skeleton
                    variant="text"
                    size="xl"
                    tone="onDark"
                    style={{ width: 160 }}
                  />
                </div>
                <Skeleton variant="text" tone="onDark" style={{ width: 240 }} />
                <Skeleton variant="text" tone="onDark" style={{ width: 180 }} />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton
                      variant="circle"
                      tone="onDark"
                      key={i}
                      style={{ width: 40, height: 40 }}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {hasVideoAvatar && videoAvatarUrl ? (
                <video
                  ref={videoRef}
                  className={s.media}
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  src={videoAvatarUrl}
                  aria-label={`${name} video`}
                />
              ) : (
                <div className={s.mediaFrame}>
                  <img
                    className={s.media}
                    src={avatarUrl}
                    alt={name}
                    width={220}
                    height={300}
                  />
                  <CharacterStack
                    className={s.mediaStack}
                    characters={characters}
                    label={CHARACTERS_LABEL}
                  />
                </div>
              )}
              <div className={s.details}>
                <div className={s.titleRow}>
                  {hasVideoAvatar && (
                    <>
                      <img
                        className={s.avatar}
                        src={avatarSquareUrl ?? avatarUrl}
                        alt={name}
                        width={96}
                        height={96}
                      />
                      <CharacterStack
                        characters={characters}
                        label={CHARACTERS_LABEL}
                      />
                    </>
                  )}
                  <h1>{name}</h1>
                  <FavouriteButton
                    influencerId={influencerId}
                    variant="light"
                  />
                </div>
                {caption && <p className={s.caption}>{caption}</p>}
                {!isLoading && (
                  <p className={s.valueProp}>
                    {productCount > 0
                      ? `${productCount} items available for personalized autographs`
                      : hasLivestreamProducts
                      ? "Personalized autographs, signed live on stream"
                      : "Personalized autographs"}
                    {nextEventDate &&
                      (hasLivestreamProducts
                        ? ` · Next live signing ${formatRelativeDate(
                            nextEventDate,
                          )}`
                        : ` · Next signing ${formatRelativeDate(
                            nextEventDate,
                          )}`)}
                  </p>
                )}
                {charityName && (
                  <div
                    className={s.charityBadge}
                    role="note"
                    aria-label={`Items purchased from this Talent will help support ${charityName}.`}
                    title={`Items purchased from this Talent will help support ${charityName}.`}
                  >
                    <LeafIcon className={s.charityBadgeIcon} />
                    <span>Supporting&nbsp;&nbsp;{charityName}</span>
                  </div>
                )}
                {!isLoading && productCount > 0 && (
                  <a href="#shop-products" className={s.heroCta}>
                    Browse Products
                  </a>
                )}
                {!isLoading && (
                  <div className={s.trustSignals}>
                    {hasLivestreamProducts && (
                      <span className={s.trustItem}>
                        <LiveIcon />
                        Signed live on stream
                      </span>
                    )}
                    <span className={s.trustItem}>
                      <PersonalizeIcon />
                      Personalized to you
                    </span>
                    <span className={s.trustItem}>
                      <ShieldCheckIcon />
                      Authenticity guaranteed
                    </span>
                  </div>
                )}
                {socials && (
                  <div className={s.socialLinks}>
                    {SOCIAL_PLATFORMS.filter(({ key }) => socials[key]).map(
                      ({ key, buildUrl, label, svgProps, paths }) => (
                        <a
                          key={key}
                          href={buildUrl(socials[key])}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            {...svgProps}
                          >
                            {paths}
                          </svg>
                        </a>
                      ),
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

ShopHero.propTypes = {
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  caption: PropTypes.string,
  avatarUrl: PropTypes.string,
  avatarSquareUrl: PropTypes.string,
  bannerUrl: PropTypes.string,
  characters: PropTypes.arrayOf(PropTypes.object),
  hasVideoAvatar: PropTypes.bool,
  videoAvatarUrl: PropTypes.string,
  charityName: PropTypes.string,
  hasLivestreamProducts: PropTypes.bool,
  socials: PropTypes.shape({
    instagram: PropTypes.string,
    twitter: PropTypes.string,
    tiktok: PropTypes.string,
    youtube: PropTypes.string,
  }),
  productCount: PropTypes.number,
  nextEventDate: PropTypes.instanceOf(Date),
  influencerId: PropTypes.number,
};
